import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Chip,
  IconButton,
  Tooltip,
  Grid,
  SelectChangeEvent,
  Alert,
  // CircularProgress,
  alpha,
  useTheme,
  Fade,
  Skeleton,
  Badge,
  Divider,
  Container,
  Avatar,
  Stack,
  useMediaQuery
} from '@mui/material';
import {
  Download,
  Search,
  Verified,
  Shield,
  Gavel,
  Security as SecurityIcon,
  Refresh as RefreshIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon,
  Assessment,
  Description
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

interface AuditLog {
  id: string;
  event_type: string;
  event_category: string;
  severity: string;
  phi_categories: string[];
  action_taken: string;
  request_ip: string;
  created_at: string;
  verified: boolean;
  metadata?: {
    rule_name?: string;
    rule_id?: string;
    framework?: string;
    [key: string]: any;
  };
}

interface AuditLogViewerProps {
  organizationId?: number;
}

const AuditLogViewer: React.FC<AuditLogViewerProps> = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [searchQuery, setSearchQuery] = useState('');
  const [eventCategory, setEventCategory] = useState<string>('');
  const [severity, setSeverity] = useState<string>('');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [showFilters, setShowFilters] = useState(true);

  const {
    data: auditData,
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: [
      'auditLogs',
      page,
      rowsPerPage,
      searchQuery,
      eventCategory,
      severity,
      startDate,
      endDate
    ],
    queryFn: async () => {
      const params: any = {
        limit: rowsPerPage,
        offset: page * rowsPerPage
      };

      if (searchQuery) {
        const response = await axios.get('/api/audit/search', {
          params: { ...params, q: searchQuery, event_category: eventCategory, severity }
        });
        return response.data;
      }

      if (eventCategory) params.event_category = eventCategory;
      if (severity) params.severity = severity;
      if (startDate) params.start_date = startDate.toISOString();
      if (endDate) params.end_date = endDate.toISOString();

      const response = await axios.get('/api/audit/logs', { params });
      return response.data;
    },
    refetchInterval: 30000 // Refetch every 30 seconds
  });

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleCategoryChange = (event: SelectChangeEvent<string>) => {
    setEventCategory(event.target.value);
    setPage(0);
  };

  const handleSeverityChange = (event: SelectChangeEvent<string>) => {
    setSeverity(event.target.value);
    setPage(0);
  };

  const handleSearch = () => {
    setPage(0);
    refetch();
  };

  const handleExport = async () => {
    try {
      const params: any = {};
      if (startDate) params.start_date = startDate.toISOString();
      if (endDate) params.end_date = endDate.toISOString();
      params.format = 'csv';

      const response = await axios.get('/api/audit/export', {
        params,
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `audit-logs-${new Date().toISOString()}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Export failed:', error);
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical':
        return 'error';
      case 'high':
        return 'error';
      case 'medium':
        return 'warning';
      case 'low':
        return 'info';
      default:
        return 'default';
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'phi_detection':
        return 'primary';
      case 'breach_attempt':
        return 'error';
      case 'access_control':
        return 'warning';
      case 'configuration_change':
        return 'info';
      default:
        return 'default';
    }
  };

  const getActionColor = (action: string) => {
    switch (action) {
      case 'block':
        return 'error';
      case 'redact':
        return 'warning';
      case 'alert':
        return 'info';
      case 'allow':
        return 'success';
      default:
        return 'default';
    }
  };

  const getFrameworkIcon = (framework?: string) => {
    if (!framework) return null;
    switch (framework) {
      case 'HIPAA':
        return <Shield fontSize="small" />;
      case 'GDPR':
        return <Gavel fontSize="small" />;
      case 'SOC2':
        return <SecurityIcon fontSize="small" />;
      case 'ISO27001':
        return <Shield fontSize="small" />;
      case 'NIST80053':
        return <SecurityIcon fontSize="small" />;
      default:
        return <SecurityIcon fontSize="small" />;
    }
  };

  const getFrameworkColor = (
    framework?: string
  ): 'error' | 'warning' | 'info' | 'success' | 'default' => {
    if (!framework) return 'default';
    switch (framework) {
      case 'HIPAA':
        return 'error';
      case 'GDPR':
        return 'warning';
      case 'SOC2':
        return 'info';
      case 'ISO27001':
        return 'success';
      case 'NIST80053':
        return 'info';
      default:
        return 'default';
    }
  };

  if (isLoading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Card
          elevation={0}
          sx={{ border: `1px solid ${alpha(theme.palette.divider, 0.1)}`, borderRadius: 3 }}
        >
          <CardContent sx={{ p: 4 }}>
            <Box mb={4}>
              <Skeleton variant="text" width={200} height={48} sx={{ borderRadius: 1 }} />
              <Skeleton variant="text" width={400} height={24} sx={{ mt: 1, borderRadius: 1 }} />
            </Box>
            <Grid container spacing={2} mb={3}>
              {[1, 2, 3, 4, 5].map((i) => (
                <Grid item xs={12} md={2.4} key={i}>
                  <Skeleton variant="rectangular" height={40} sx={{ borderRadius: 2 }} />
                </Grid>
              ))}
            </Grid>
            <Box>
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton
                  key={i}
                  variant="rectangular"
                  height={60}
                  sx={{ mb: 1, borderRadius: 1 }}
                />
              ))}
            </Box>
          </CardContent>
        </Card>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Card
          elevation={0}
          sx={{ border: `1px solid ${alpha(theme.palette.divider, 0.1)}`, borderRadius: 3 }}
        >
          <CardContent sx={{ p: 4 }}>
            <Alert
              severity="error"
              sx={{
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
                '& .MuiAlert-icon': {
                  fontSize: 28
                }
              }}
            >
              <Typography variant="h6" gutterBottom fontWeight={700}>
                Failed to Load Audit Logs
              </Typography>
              <Typography variant="body2">
                Unable to retrieve audit logs. Please check your connection and try again.
              </Typography>
              <Button variant="outlined" size="small" onClick={() => refetch()} sx={{ mt: 2 }}>
                Retry
              </Button>
            </Alert>
          </CardContent>
        </Card>
      </Container>
    );
  }

  const activeFiltersCount = [eventCategory, severity, startDate, endDate, searchQuery].filter(
    Boolean
  ).length;

  const handleClearFilters = () => {
    setSearchQuery('');
    setEventCategory('');
    setSeverity('');
    setStartDate(null);
    setEndDate(null);
    setPage(0);
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Enhanced Header with Gradient Background */}
      <Fade in timeout={600}>
        <Box
          sx={{
            mb: 4,
            p: 4,
            borderRadius: 3,
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              right: 0,
              width: '300px',
              height: '300px',
              background: `radial-gradient(circle, ${alpha(theme.palette.primary.main, 0.1)} 0%, transparent 70%)`,
              borderRadius: '50%',
              transform: 'translate(30%, -30%)'
            }
          }}
        >
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
            gap={3}
          >
            <Box display="flex" alignItems="center" gap={2.5}>
              <Avatar
                sx={{
                  width: 64,
                  height: 64,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                  boxShadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.4)}`
                }}
              >
                <Description sx={{ fontSize: 32, color: 'white' }} />
              </Avatar>
              <Box>
                <Typography
                  variant={isMobile ? 'h5' : 'h4'}
                  fontWeight={700}
                  sx={{
                    mb: 0.5,
                    background: `linear-gradient(135deg, ${theme.palette.text.primary} 0%, ${theme.palette.primary.main} 100%)`,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent'
                  }}
                >
                  Audit Logs
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                >
                  <Assessment sx={{ fontSize: 16 }} />
                  Comprehensive security and compliance audit trail
                </Typography>
              </Box>
            </Box>

            <Stack direction="row" spacing={1.5} flexWrap="wrap">
              <Tooltip title="Refresh Data" arrow>
                <IconButton
                  onClick={() => refetch()}
                  sx={{
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    '&:hover': {
                      bgcolor: alpha(theme.palette.primary.main, 0.2),
                      transform: 'rotate(180deg)'
                    },
                    transition: 'all 0.5s ease'
                  }}
                >
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title={showFilters ? 'Hide Filters' : 'Show Filters'} arrow>
                <IconButton
                  onClick={() => setShowFilters(!showFilters)}
                  sx={{
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    '&:hover': {
                      bgcolor: alpha(theme.palette.primary.main, 0.2)
                    }
                  }}
                >
                  <Badge badgeContent={activeFiltersCount} color="primary">
                    <FilterIcon />
                  </Badge>
                </IconButton>
              </Tooltip>
              <Button
                variant="contained"
                startIcon={<Download />}
                onClick={handleExport}
                size={isMobile ? 'small' : 'medium'}
                sx={{
                  background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                  boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                  '&:hover': {
                    background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
                    transform: 'translateY(-2px)',
                    boxShadow: `0 6px 16px ${alpha(theme.palette.primary.main, 0.4)}`
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                Export
              </Button>
            </Stack>
          </Box>
        </Box>
      </Fade>

      <Fade in timeout={600}>
        <Card
          elevation={0}
          sx={{
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            borderRadius: 3,
            overflow: 'hidden'
          }}
        >
          <CardContent sx={{ p: 4 }}>
            {/* Enhanced Filters */}
            {showFilters && (
              <Fade in timeout={400}>
                <Box
                  sx={{
                    mb: 4,
                    p: 3,
                    borderRadius: 2.5,
                    bgcolor: alpha(theme.palette.background.default, 0.6),
                    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`
                  }}
                >
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2.5}>
                    <Box display="flex" alignItems="center" gap={1.5}>
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: 1.5,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                          boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`
                        }}
                      >
                        <FilterIcon sx={{ color: 'white', fontSize: 20 }} />
                      </Box>
                      <Typography variant="h6" fontWeight={700}>
                        Filter Logs
                      </Typography>
                    </Box>
                    {activeFiltersCount > 0 && (
                      <Button
                        size="small"
                        startIcon={<ClearIcon />}
                        onClick={handleClearFilters}
                        sx={{
                          textTransform: 'none',
                          fontWeight: 600
                        }}
                      >
                        Clear All ({activeFiltersCount})
                      </Button>
                    )}
                  </Box>
                  <Divider sx={{ mb: 3, opacity: 0.6 }} />
                  <Grid container spacing={2.5}>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Search Logs"
                        placeholder="Search by event type, IP, or details..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                        InputProps={{
                          endAdornment: (
                            <IconButton
                              size="small"
                              onClick={handleSearch}
                              sx={{
                                bgcolor: alpha(theme.palette.primary.main, 0.1),
                                '&:hover': {
                                  bgcolor: alpha(theme.palette.primary.main, 0.2)
                                }
                              }}
                            >
                              <Search fontSize="small" />
                            </IconButton>
                          )
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            bgcolor: 'background.paper',
                            '&:hover': {
                              boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.08)}`
                            },
                            '&.Mui-focused': {
                              boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.16)}`
                            }
                          }
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} md={2}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Category</InputLabel>
                        <Select
                          value={eventCategory}
                          onChange={handleCategoryChange}
                          label="Category"
                          sx={{
                            bgcolor: 'background.paper',
                            '&:hover': {
                              boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.08)}`
                            },
                            '&.Mui-focused': {
                              boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.16)}`
                            }
                          }}
                        >
                          <MenuItem value="">All Categories</MenuItem>
                          <MenuItem value="phi_detection">PHI Detection</MenuItem>
                          <MenuItem value="breach_attempt">Breach Attempt</MenuItem>
                          <MenuItem value="access_control">Access Control</MenuItem>
                          <MenuItem value="configuration_change">Configuration</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} md={2}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Severity</InputLabel>
                        <Select
                          value={severity}
                          onChange={handleSeverityChange}
                          label="Severity"
                          sx={{
                            bgcolor: 'background.paper',
                            '&:hover': {
                              boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.08)}`
                            },
                            '&.Mui-focused': {
                              boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.16)}`
                            }
                          }}
                        >
                          <MenuItem value="">All Severities</MenuItem>
                          <MenuItem value="critical">Critical</MenuItem>
                          <MenuItem value="high">High</MenuItem>
                          <MenuItem value="medium">Medium</MenuItem>
                          <MenuItem value="low">Low</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} md={2}>
                      <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DatePicker
                          label="Start Date"
                          value={startDate}
                          onChange={(newValue) => setStartDate(newValue)}
                          slotProps={{
                            textField: {
                              size: 'small',
                              fullWidth: true,
                              sx: {
                                '& .MuiOutlinedInput-root': {
                                  bgcolor: 'background.paper',
                                  '&:hover': {
                                    boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.08)}`
                                  },
                                  '&.Mui-focused': {
                                    boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.16)}`
                                  }
                                }
                              }
                            }
                          }}
                        />
                      </LocalizationProvider>
                    </Grid>

                    <Grid item xs={12} md={2}>
                      <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DatePicker
                          label="End Date"
                          value={endDate}
                          onChange={(newValue) => setEndDate(newValue)}
                          slotProps={{
                            textField: {
                              size: 'small',
                              fullWidth: true,
                              sx: {
                                '& .MuiOutlinedInput-root': {
                                  bgcolor: 'background.paper',
                                  '&:hover': {
                                    boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.08)}`
                                  },
                                  '&.Mui-focused': {
                                    boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.16)}`
                                  }
                                }
                              }
                            }
                          }}
                        />
                      </LocalizationProvider>
                    </Grid>
                  </Grid>
                </Box>
              </Fade>
            )}

            {/* Enhanced Table */}
            <Box
              sx={{
                borderRadius: 2,
                overflow: 'hidden',
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`
              }}
            >
              <Box
                sx={{
                  p: 2.5,
                  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.primary.dark, 0.05)} 100%)`,
                  borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.1)}`
                }}
              >
                <Typography variant="h6" fontWeight={700}>
                  Audit Trail
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {auditData?.total || 0} log entries found
                </Typography>
              </Box>
              <TableContainer sx={{ maxHeight: 'calc(100vh - 550px)', minHeight: 400 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{
                          fontWeight: 700,
                          bgcolor: 'background.paper',
                          borderBottom: `2px solid ${theme.palette.divider}`,
                          fontSize: '0.813rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          color: 'primary.main'
                        }}
                      >
                        Timestamp
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 700,
                          bgcolor: 'background.paper',
                          borderBottom: `2px solid ${theme.palette.divider}`,
                          fontSize: '0.813rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          color: 'primary.main'
                        }}
                      >
                        Event Type
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 700,
                          bgcolor: 'background.paper',
                          borderBottom: `2px solid ${theme.palette.divider}`,
                          fontSize: '0.813rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          color: 'primary.main'
                        }}
                      >
                        Category
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 700,
                          bgcolor: 'background.paper',
                          borderBottom: `2px solid ${theme.palette.divider}`,
                          fontSize: '0.813rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          color: 'primary.main'
                        }}
                      >
                        Rule
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 700,
                          bgcolor: 'background.paper',
                          borderBottom: `2px solid ${theme.palette.divider}`,
                          fontSize: '0.813rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          color: 'primary.main'
                        }}
                      >
                        Severity
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 700,
                          bgcolor: 'background.paper',
                          borderBottom: `2px solid ${theme.palette.divider}`,
                          fontSize: '0.813rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          color: 'primary.main'
                        }}
                      >
                        Action
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 700,
                          bgcolor: 'background.paper',
                          borderBottom: `2px solid ${theme.palette.divider}`,
                          fontSize: '0.813rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          color: 'primary.main'
                        }}
                      >
                        PHI Categories
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 700,
                          bgcolor: 'background.paper',
                          borderBottom: `2px solid ${theme.palette.divider}`,
                          fontSize: '0.813rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          color: 'primary.main'
                        }}
                      >
                        IP Address
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{
                          fontWeight: 700,
                          bgcolor: 'background.paper',
                          borderBottom: `2px solid ${theme.palette.divider}`,
                          fontSize: '0.813rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          color: 'primary.main'
                        }}
                      >
                        Verified
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {auditData?.logs?.map((log: AuditLog) => {
                      // Extract rule and framework info from metadata
                      const ruleName = log.metadata?.rule_name;
                      const framework = log.metadata?.framework;

                      return (
                        <TableRow
                          key={log.id}
                          hover
                          sx={{
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            borderLeft: `3px solid transparent`,
                            '&:hover': {
                              bgcolor: alpha(theme.palette.primary.main, 0.04),
                              transform: 'scale(1.001)',
                              boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.08)}`,
                              borderLeft: `3px solid ${theme.palette.primary.main}`
                            },
                            '&:last-child td': {
                              borderBottom: 0
                            }
                          }}
                        >
                          <TableCell sx={{ py: 2 }}>
                            <Typography
                              variant="body2"
                              fontWeight={500}
                              sx={{ fontSize: '0.813rem' }}
                            >
                              {new Date(log.created_at).toLocaleString()}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ py: 2 }}>
                            <Typography
                              variant="body2"
                              noWrap
                              fontWeight={500}
                              sx={{ maxWidth: 200 }}
                            >
                              {log.event_type}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ py: 2 }}>
                            <Chip
                              label={log.event_category}
                              size="small"
                              color={getCategoryColor(log.event_category)}
                              sx={{
                                fontWeight: 600,
                                fontSize: '0.75rem'
                              }}
                            />
                          </TableCell>
                          <TableCell sx={{ py: 2 }}>
                            {ruleName ? (
                              <Box>
                                <Chip
                                  icon={getFrameworkIcon(framework) || undefined}
                                  label={ruleName}
                                  size="small"
                                  color={getFrameworkColor(framework)}
                                  variant="outlined"
                                  sx={{
                                    fontWeight: 600,
                                    fontSize: '0.75rem',
                                    borderWidth: 1.5
                                  }}
                                />
                                {framework && (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    display="block"
                                    sx={{ mt: 0.5, fontSize: '0.688rem' }}
                                  >
                                    {framework}
                                  </Typography>
                                )}
                              </Box>
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                -
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell sx={{ py: 2 }}>
                            <Chip
                              label={log.severity}
                              size="small"
                              color={getSeverityColor(log.severity)}
                              sx={{
                                fontWeight: 600,
                                fontSize: '0.75rem'
                              }}
                            />
                          </TableCell>
                          <TableCell sx={{ py: 2 }}>
                            <Chip
                              label={log.action_taken}
                              size="small"
                              color={getActionColor(log.action_taken)}
                              sx={{
                                fontWeight: 600,
                                fontSize: '0.75rem'
                              }}
                            />
                          </TableCell>
                          <TableCell sx={{ py: 2 }}>
                            {log.phi_categories && log.phi_categories.length > 0 ? (
                              <Box display="flex" gap={0.5} flexWrap="wrap">
                                {log.phi_categories.slice(0, 2).map((cat, idx) => (
                                  <Chip
                                    key={idx}
                                    label={cat}
                                    size="small"
                                    variant="outlined"
                                    sx={{
                                      fontSize: '0.688rem',
                                      height: 22
                                    }}
                                  />
                                ))}
                                {log.phi_categories.length > 2 && (
                                  <Chip
                                    label={`+${log.phi_categories.length - 2}`}
                                    size="small"
                                    variant="outlined"
                                    sx={{
                                      fontSize: '0.688rem',
                                      height: 22,
                                      bgcolor: alpha(theme.palette.primary.main, 0.08)
                                    }}
                                  />
                                )}
                              </Box>
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                -
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell sx={{ py: 2 }}>
                            <Typography
                              variant="body2"
                              noWrap
                              fontWeight={500}
                              sx={{
                                fontFamily: 'monospace',
                                fontSize: '0.813rem'
                              }}
                            >
                              {log.request_ip || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell align="center" sx={{ py: 2 }}>
                            {log.verified ? (
                              <Tooltip
                                title="Cryptographic signature verified"
                                arrow
                                placement="left"
                              >
                                <Verified
                                  sx={{
                                    color: 'success.main',
                                    fontSize: 22
                                  }}
                                />
                              </Tooltip>
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                -
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>

            {/* Enhanced Pagination */}
            <Box
              sx={{
                mt: 3,
                pt: 2.5,
                borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`
              }}
            >
              <TablePagination
                rowsPerPageOptions={[10, 25, 50, 100]}
                component="div"
                count={auditData?.total || 0}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                sx={{
                  '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                    fontWeight: 600
                  }
                }}
              />
            </Box>
          </CardContent>
        </Card>
      </Fade>
    </Container>
  );
};

export default AuditLogViewer;
